import React, { ChangeEvent, useState } from 'react';
import { Box, Flex, Text } from '@chakra-ui/react';
import { ReactComponent as LeftArrowIcon } from '../../assets/images/icons/mevo/left-arrow.svg';
import { ReactComponent as RightArrowIcon } from '../../assets/images/icons/right-arrow.svg';
import { useIsMobile } from '../../helpers/useIsMobile';
import { validateBirthDate, validateEmail } from '../../helpers/validations';
import { Button } from '../UI-CHAKRA/Button';
import { Checkbox } from '../UI-CHAKRA/Checkbox';
import { Input } from '../UI-CHAKRA/Input/Input';
import { MaskedInput, MASKS } from '../UI-CHAKRA/Input/MaskedInput';
import { FormControl } from '../UI-CHAKRA/FormControl';
import { FormLabel } from '../UI-CHAKRA/FormLabel';
import { FormErrorMessage } from '../UI-CHAKRA/FormErrorMessage';
import { FreeSampleMedicine } from '../../interfaces/freeSample';
import { isValidSpecialty } from '../../helpers/rqeSpecialties';
import FreeSampleSpecialtyCombobox from './FreeSampleSpecialtyCombobox';
import FreeSampleHeader from './FreeSampleHeader';

interface FreeSampleInterestStepProps {
  brand: string;
  item: FreeSampleMedicine;
  email: string;
  phone: string;
  birthdate: string;
  onEmailChange: (value: string) => void;
  onPhoneChange: (value: string) => void;
  onBirthdateChange: (value: string) => void;
  specialty: string;
  onSpecialtyChange: (value: string) => void;
  isResolvingSpecialty: boolean;
  shippingConsent: boolean;
  communicationsConsent: boolean;
  onShippingConsentChange: (checked: boolean) => void;
  onCommunicationsConsentChange: (checked: boolean) => void;
  isLoading: boolean;
  onBack: () => void;
  onConfirm: () => void;
  onClose: () => void;
}

// Fluxo de "interesse" (Figma, seção "Interesse"): quando o médico não é elegível pra marca, ele
// não escolhe SKU — registra interesse num único produto por vez. Vira, no backend, um item sem
// `sku` em items[] (POST /product/free-samples/request), classificado como
// ELIGIBILITY_INCLUSION_REQUEST. E-mail/telefone/nascimento vêm pré-preenchidos do cadastro do
// médico (quando existem), mas são editáveis aqui (o médico pode querer registrar um contato
// diferente pra esse interesse específico). O nascimento é obrigatório no backend, assim como a
// especialidade — pré-preenchida pelo idRqe e editável só com opções da lista.
const FreeSampleInterestStep: React.FC<FreeSampleInterestStepProps> = ({
  brand,
  item,
  email,
  phone,
  birthdate,
  onEmailChange,
  onPhoneChange,
  onBirthdateChange,
  specialty,
  onSpecialtyChange,
  isResolvingSpecialty,
  shippingConsent,
  communicationsConsent,
  onShippingConsentChange,
  onCommunicationsConsentChange,
  isLoading,
  onBack,
  onConfirm,
  onClose,
}) => {
  const isMobile = useIsMobile();
  const [birthdateTouched, setBirthdateTouched] = useState(false);
  const [emailTouched, setEmailTouched] = useState(false);
  const [phoneTouched, setPhoneTouched] = useState(false);
  const [specialtyTouched, setSpecialtyTouched] = useState(false);
  const isBirthdateValid = validateBirthDate(birthdate);
  const isEmailValid = validateEmail(email);
  // Conta só dígitos: com a máscara o campo "vazio" ainda tem os placeholders, ex.: (__) _ ____-____.
  const isPhoneValid = phone.replace(/\D/g, '').length >= 10;
  const isSpecialtyValid = isValidSpecialty(specialty);
  const isConfirmDisabled =
    !shippingConsent ||
    !isEmailValid ||
    !isPhoneValid ||
    !isBirthdateValid ||
    !isSpecialtyValid ||
    isResolvingSpecialty;

  return (
    <Flex flexDirection={'column'} height={'100%'} overflow="hidden">
      {isMobile ? (
        <Box flexShrink={0} borderBottom={'1px solid #E9ECEF'}>
          <Flex flexDirection={'row'} alignItems={'center'} gap={'8px'} padding={'20px'}>
            <LeftArrowIcon onClick={onBack} cursor={'pointer'} />
            <Text fontSize={'14px'} fontWeight={700} color={'primary.main'}>
              Voltar
            </Text>
          </Flex>
        </Box>
      ) : (
        <FreeSampleHeader onClose={onClose} />
      )}

      <Box flexGrow={1} overflowY={'auto'} overflowX="hidden" padding={'20px'}>
        <Text fontSize={'12px'} fontWeight={700} color={'#6C757D'}>
          LABORATÓRIO
        </Text>
        <Text fontSize={'16px'} fontWeight={700} color={'#343A40'} mb={'12px'}>
          {brand}
        </Text>

        <Text fontSize={'12px'} fontWeight={700} color={'#6C757D'}>
          PRODUTO
        </Text>
        <Text fontSize={'16px'} fontWeight={700} color={'#343A40'} mb={'16px'}>
          {item.medicineName}
        </Text>

        <Text fontSize={'14px'} color={'#6C757D'} mb={'20px'}>
          Seu interesse é encaminhado ao laboratório, que define quais prescritores recebem amostras.
        </Text>

        <Flex flexDirection={'column'} gap={'16px'} mb={'20px'}>
          <FormControl isInvalid={emailTouched && !isEmailValid}>
            <FormLabel>E-mail</FormLabel>
            <Input
              value={email}
              onChange={(e) => onEmailChange(e.target.value)}
              onBlur={() => setEmailTouched(true)}
              placeholder={'E-mail'}
              type={'email'}
            />
            <FormErrorMessage>Informe um e-mail válido</FormErrorMessage>
          </FormControl>
          <FormControl isInvalid={phoneTouched && !isPhoneValid}>
            <FormLabel>Telefone</FormLabel>
            <MaskedInput
              mask={MASKS.BRAZIL_PHONE}
              value={phone}
              onChange={(e) => onPhoneChange(e.target.value)}
              onBlur={() => setPhoneTouched(true)}
              placeholder={'(11) 98765-4321'}
            />
            <FormErrorMessage>Informe um telefone válido</FormErrorMessage>
          </FormControl>
          <FormControl isInvalid={birthdateTouched && !isBirthdateValid}>
            <FormLabel>Data de nascimento</FormLabel>
            <MaskedInput
              mask={MASKS.BIRTH_DATE}
              maskChar={''}
              value={birthdate}
              onChange={(e: ChangeEvent<HTMLInputElement>) => onBirthdateChange(e.target.value)}
              onBlur={() => setBirthdateTouched(true)}
              placeholder={'dd/mm/aaaa'}
            />
            <FormErrorMessage>Informe uma data válida</FormErrorMessage>
          </FormControl>
          <FormControl isInvalid={!isResolvingSpecialty && specialtyTouched && !isSpecialtyValid}>
            <FreeSampleSpecialtyCombobox
              label={'Especialidade'}
              value={specialty}
              onChange={onSpecialtyChange}
              onBlur={() => setSpecialtyTouched(true)}
              isLoading={isResolvingSpecialty}
            />
            <FormErrorMessage>Selecione sua especialidade</FormErrorMessage>
          </FormControl>
        </Flex>

        <Flex flexDirection={'column'} gap={'16px'}>
          <Checkbox
            isChecked={shippingConsent}
            onChange={(e) => onShippingConsentChange(e.target.checked)}
            size="md"
            alignItems={'flex-start'}
          >
            <Text fontSize={'14px'} color={'#343A40'} ml={'8px'} lineHeight={'20px'}>
              Autorizo a Mevo a compartilhar meus dados com{' '}
              <Text as="span" color={'primary.main'} textDecoration={'underline'}>
                {brand}
              </Text>{' '}
              para registrar meu interesse em receber amostras.
            </Text>
          </Checkbox>

          <Checkbox
            isChecked={communicationsConsent}
            onChange={(e) => onCommunicationsConsentChange(e.target.checked)}
            size="md"
            alignItems={'flex-start'}
          >
            <Text fontSize={'14px'} color={'#343A40'} ml={'8px'} lineHeight={'20px'}>
              Quero receber avisos de{' '}
              <Text as="span" color={'primary.main'} textDecoration={'underline'}>
                {brand}
              </Text>{' '}
              sobre novas amostras e programas para prescritores.
              <Text as="span" color={'#6C757D'}>
                {' '}
                (opcional)
              </Text>
            </Text>
          </Checkbox>
        </Flex>
      </Box>

      <Box flexShrink={0} padding={'20px'} borderTop={'1px solid #E9ECEF'}>
        <Flex flexDirection={'row'} gap={'12px'} justifyContent={'flex-end'}>
          <Button
            onClick={onBack}
            padding="10px 20px"
            backgroundColor={'#FFF'}
            borderWidth={'2px'}
            borderColor={'primary.main'}
            borderStyle={'solid'}
            borderRadius={'20px'}
            _hover={{ backgroundColor: '#FFF', borderColor: '#6B409C' }}
          >
            <Text fontSize={'14px'} color={'primary.main'}>
              Voltar
            </Text>
          </Button>
          <Button
            onClick={onConfirm}
            isDisabled={isConfirmDisabled || isLoading}
            isLoading={isLoading}
            padding="10px 20px"
            backgroundColor={'primary.main'}
            borderRadius={'20px'}
            gap={'8px'}
            color={'#FFF'}
            fontSize={'14px'}
            _hover={{ backgroundColor: '#6B409C' }}
            _disabled={{ opacity: 0.5, cursor: 'not-allowed' }}
            {...(!isLoading && { rightIcon: <RightArrowIcon width={'16px'} height={'16px'} fill="#FFF" /> })}
          >
            {isLoading ? 'Enviando...' : 'Enviar interesse'}
          </Button>
        </Flex>
      </Box>
    </Flex>
  );
};

export default FreeSampleInterestStep;
